/**
 * Nav-domain registry. Single source of truth for the "view by"
 * filter system.
 *
 * Concepts:
 *   - NAV_DOMAINS: every domain identifier an item can be tagged with.
 *     Items in nav-groups.tsx MUST carry at least one of these in their
 *     `domains: NavDomain[]` field. Add new domains here as the product
 *     grows (ipp, fog, stormwater, sdwa, etc.).
 *   - ACTIVE_VIEWS: the subset of domains currently exposed as
 *     dropdown options. Items may be tagged with domains that aren't
 *     active yet — the data is forward-compatible. Activate a domain
 *     by adding it here AND adding a `view.<domain>` i18n key.
 *   - NavView: union of "all" + NavDomain. "all" is a wildcard that
 *     bypasses the tag check (every item shows).
 *
 * Filter semantics (see filterNavGroupsByView):
 *   - activeView === "all"  → every item shows
 *   - activeView === D      → only items whose `domains` includes D
 */
export const NAV_DOMAINS = ["backflow", "sampling", "compliance", "admin"] as const;

export type NavDomain = (typeof NAV_DOMAINS)[number];

/**
 * Domains currently exposed as dropdown options. Today: just backflow.
 * Items can be tagged with non-active domains — they simply aren't
 * filterable yet.
 */
export const ACTIVE_VIEWS: readonly NavDomain[] = ["backflow"] as const;

export type NavView = "all" | NavDomain;

/**
 * The default selected view. The deployed app focuses on Backflow only, so
 * fresh loads (and any stale persisted value) resolve to this rather than the
 * generic "all" wildcard.
 */
export const DEFAULT_VIEW: NavView = "backflow";

/**
 * Dropdown options. The "all" wildcard — which reveals every page regardless
 * of its domain tag, including pages still under construction — is exposed
 * ONLY in local debug builds (import.meta.env.DEV). Deployed builds list just
 * the ACTIVE_VIEWS, so there is no way to switch to "all".
 */
export const NAV_VIEWS: readonly NavView[] = import.meta.env.DEV
  ? (["all", ...ACTIVE_VIEWS] as const)
  : ACTIVE_VIEWS;

export function isNavView(value: unknown): value is NavView {
  return typeof value === "string" && (NAV_VIEWS as readonly string[]).includes(value);
}

/**
 * Filter a nav-group list by the active view. "all" passes everything;
 * any other view keeps items whose `domains` array contains it, and
 * drops groups that go empty.
 *
 * Generic over the group / item shape so the helper can be unit-tested
 * with minimal fixtures and so it stays decoupled from the full
 * sidebar NavItem/NavGroup definitions (which carry icons, route
 * paths, etc. that the filter doesn't care about).
 */
export function filterNavGroupsByView<
  I extends { readonly domains: readonly NavDomain[] },
  G extends { readonly items: readonly I[] },
>(groups: readonly G[], activeView: NavView): G[] {
  if (activeView === "all") return [...groups];
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.domains.includes(activeView)),
    }))
    .filter((group) => group.items.length > 0);
}
